/**
 * use-progressive-number.ts
 *
 * Hook per la gestione del Numero Progressivo (N/ANNO).
 * Fornisce suggerimenti automatici e validazione in tempo reale.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { requestService } from '../services/api/requests-new';
import { useCacheConfig } from '../contexts/cache-config-context';
import {
  RequestType,
  ProgressiveNumberSuggestion,
  ProgressiveNumberValidation
} from '../types/api';

/**
 * Hook per ottenere il suggerimento del prossimo numero progressivo disponibile
 */
export function useProgressiveNumberSuggestion(
  requestType: RequestType | undefined,
  year?: number,
  enabled: boolean = true
) {
  const { config } = useCacheConfig();

  const query = useQuery({
    queryKey: ['progressive-number-suggestion', requestType, year],
    queryFn: () => {
      if (requestType === undefined) {
        throw new Error('Request type is required');
      }
      return requestService.getProgressiveNumberSuggestion(requestType, year);
    },
    enabled: enabled && requestType !== undefined,
    staleTime: 30000, // 30 secondi - i suggerimenti possono cambiare frequentemente
    gcTime: 60000,
  });

  return {
    suggestion: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Hook per validare un numero progressivo
 */
export function useValidateProgressiveNumber() {
  const mutation = useMutation({
    mutationFn: ({
      number,
      year,
      requestType,
      excludeRequestId
    }: {
      number: number;
      year: number;
      requestType: RequestType;
      excludeRequestId?: number;
    }) => requestService.validateProgressiveNumber(number, year, requestType, excludeRequestId),
  });

  return {
    validate: mutation.mutateAsync,
    isValidating: mutation.isPending,
    validation: mutation.data,
    error: mutation.error,
    isError: mutation.isError,
    reset: mutation.reset,
  };
}

/**
 * Hook combinato per gestire il numero progressivo nel form
 */
export function useProgressiveNumberForm(
  requestType: RequestType | undefined,
  excludeRequestId?: number
) {
  const currentYear = new Date().getFullYear();

  const {
    suggestion,
    isLoading: isLoadingSuggestion,
    refetch: refetchSuggestion
  } = useProgressiveNumberSuggestion(requestType, currentYear);

  const {
    validate,
    isValidating,
    validation,
    reset: resetValidation
  } = useValidateProgressiveNumber();

  const validateNumber = async (number: number, year: number) => {
    if (!requestType) return null;

    try {
      const result = await validate({
        number,
        year,
        requestType,
        excludeRequestId
      });
      return result;
    } catch (error) {
      return {
        isAvailable: false,
        errorMessage: 'Errore durante la validazione'
      } as ProgressiveNumberValidation;
    }
  };

  return {
    // Suggerimento
    suggestion,
    isLoadingSuggestion,
    refetchSuggestion,

    // Validazione
    validateNumber,
    isValidating,
    validation,
    resetValidation,

    // Valori di default
    defaultYear: currentYear,
    defaultNumber: suggestion?.number ?? 1,
  };
}
